/* eslint no-shadow: ["error", { "allow": ["state", "mutations", "getters"] }] */
// https://github.com/vuejs/vuex/issues/676#issuecomment-339855002
// https://stackoverflow.com/a/43843973/7683994

import createPersistedState from 'vuex-persistedstate';
import Vue from 'vue';
import Vuex from 'vuex';
import {
  filter,
  isNil,
  groupBy,
  reduce,
  includes,
  toUpper,
  some,
  deburr,
  values,
  map,
  sortBy,
  flatMap,
} from 'lodash';

import { getSlottedProjects, getAttributeValueFrom } from '@/util';
import login from './modules/login';

Vue.use(Vuex);

export const state = {
  projects: [],
  projectSearchText: '',
  projectGroupsFullViewMode: false,
  locale: 'es',
};

export const getters = {
  projectGroups: (state) => {
    const isNotWebProject = object => object.type !== 'WEB';
    const projectsWithoutWeb = filter(state.projects, isNotWebProject);

    const hasProjectGroup = object => !isNil(object.projectGroup);
    const projectsWithGroup = filter(projectsWithoutWeb, hasProjectGroup);

    // group1: projects with project group
    let group1 = groupBy(projectsWithGroup, 'projectGroup');

    // from { projectGroup: [projects] } to [[projects]]
    group1 = values(group1);
    // from [[projects]] to [{projects, slots}]
    group1 = map(group1, value => ({
      projects: value,
      slots: getSlottedProjects(value),
    }));
    // from [{projects, slots}]
    // to [{projects, slots, name, description, keywords, headerLeft}]
    group1 = map(group1, (value) => {
      const firstSlot = value.slots && value.slots[0];

      const keywords = firstSlot && firstSlot.keywords;
      const keywordsList = keywords && keywords.split(',');

      const headerLeft = firstSlot && firstSlot.headerLeft;
      const headerLeftValue = headerLeft && getAttributeValueFrom(headerLeft, 'src');

      const name = firstSlot && firstSlot.name;
      const description = firstSlot && firstSlot.description;

      return {
        ...value,
        name: name || '',
        description: description || '',
        keywords: keywordsList || [],
        headerLeft: headerLeftValue || '',
      };
    });

    // group2: projects without project group
    const hasNoProjectGroup = object => isNil(object.projectGroup);
    const projectsWithoutGroup = filter(projectsWithoutWeb, hasNoProjectGroup);
    const group2 = reduce(projectsWithoutGroup, (result, project) => {
      result.push({
        projects: [project],
        name: project.name,
        description: project.description,
        slots: [{
          ...project,
          projects: [project],
        }],
        keywords: [],
      });
      return result;
    }, []);

    return sortBy([...group1, ...group2], ['name']);
  },
  projectGroupsByNameOrProjectDescription: (state, getters) =>
    function filterProjectGroupsBy(searchText) {
      return filter(getters.projectGroups, (projectGroup) => {
        const PROJECT_GROUP_NAME_INCLUDES_SEARCH_TEXT = includes(
          deburr(toUpper(projectGroup.name)),
          deburr(toUpper(searchText)),
        );

        const SOME_PROJECT_DESCRIPTION_INCLUDES_SEARCH_TEXT = some(
          projectGroup.projects,
          project => includes(
            deburr(toUpper(project.description)),
            deburr(toUpper(searchText)),
          ),
        );

        const SOME_PROJECT_KEYWORDS_INCLUDES_SEARCH_TEXT = some(
          projectGroup.projects,
          project => includes(
            deburr(toUpper(project.keywords)),
            deburr(toUpper(searchText)),
          ),
        );

        const SOME_PROJECT_PROFILE_NAME_INCLUDES_SEARCH_TEXT = some(
          flatMap(projectGroup.projects, 'profiler.profileEntity.name'),
          profileName => includes(
            deburr(toUpper(profileName)),
            deburr(toUpper(searchText)),
          ),
        );

        return PROJECT_GROUP_NAME_INCLUDES_SEARCH_TEXT ||
          SOME_PROJECT_DESCRIPTION_INCLUDES_SEARCH_TEXT ||
          SOME_PROJECT_KEYWORDS_INCLUDES_SEARCH_TEXT ||
          SOME_PROJECT_PROFILE_NAME_INCLUDES_SEARCH_TEXT;
      });
    },
  webAndErpProjects: (state) => {
    const isErpOrWebProject = object => object.type === 'WEB' || object.type === 'APP';
    const webAndErpProjects = filter(state.projects, isErpOrWebProject);

    return webAndErpProjects;
  },
};

export const mutations = {
  setProjects(state, projects = []) {
    state.projects = projects;
  },
  setProjectSearchText(state, projectSearchText = '') {
    state.projectSearchText = projectSearchText;
  },
  toggleProjectGroupsFullViewMode(state) {
    state.projectGroupsFullViewMode = !state.projectGroupsFullViewMode;
  },
  setLocale(state, locale = 'es') {
    state.locale = locale;
  },
};

export default new Vuex.Store({
  plugins: [createPersistedState({
    key: 'scu_access_app_v1.1->',
  })],
  state,
  getters,
  mutations,
  modules: {
    login,
  },
});
